import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { ONBOARDING_EVENT, capture } from '../analytics/posthog';
import styles from './OnboardingInitial.module.css';
import config, { logger } from '../config';
import { getToken, updateUserName, handleApiError } from '../utils/api';
import BrandLogo from './BrandLogo';
import logoImage from '../assets/logo.png';

const OnboardingInitial = ({ initialName, onContinue, onBack }) => {
  const embedded = typeof onContinue === 'function';
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [userName, setUserName] = useState(() => (typeof initialName === 'string' ? initialName : ''));
  const [originalName, setOriginalName] = useState(() => (typeof initialName === 'string' ? initialName : ''));

  // Signed-in visits still load the saved account name. Signup and first-time
  // Google visits pass the name in and continue without an account yet.
  useEffect(() => {
    if (embedded) return undefined;
    const savedName = localStorage.getItem(config.STORAGE_KEYS.NAME);
    if (savedName) {
      setUserName(savedName);
      setOriginalName(savedName);
    }
    return undefined;
  }, [embedded]);

  // Check if name has been modified
  const hasNameChanged = () => {
    return userName.trim() !== originalName.trim();
  };

  const handleBack = async () => {
    if (embedded) {
      onBack?.();
      return;
    }

    try {
      // Only update user name on backend if it has changed
      if (userName.trim() && hasNameChanged()) {
        const token = getToken();
        if (!token) {
          handleApiError(new Error('No token found'), navigate);
          return;
        }

        await updateUserName(token, userName.trim());
      }

      navigate('/home');
    } catch (error) {
      logger.error('Error updating user name:', error);
      handleApiError(error, navigate);
    }
  };

  const handleContinue = async () => {
    const name = userName.trim();
    if (!name) return;

    if (embedded) {
      capture(posthog, ONBOARDING_EVENT, { step: 'name_continue' });
      await onContinue(name);
      return;
    }

    try {
      // Only update user name on backend if it has changed
      if (hasNameChanged()) {
        const token = getToken();
        if (!token) {
          handleApiError(new Error('No token found'), navigate);
          return;
        }

        await updateUserName(token, name, navigate, false);
      }

      localStorage.setItem(config.STORAGE_KEYS.NAME, name);

      capture(posthog, ONBOARDING_EVENT, { step: 'welcome_continue' });
      navigate('/v2-onboarding-agent');
    } catch (error) {
      logger.error('Error updating user name:', error);
      handleApiError(error, navigate, false);
    }
  };

  const handleInputChange = (e) => {
    setUserName(e.target.value);
  };

  return (
    <main className={styles['onboarding-container']} aria-label="Onboarding - Introduce yourself">
      <div className={styles['onboarding-item']}>
        <article className={styles['onboarding-card']}>
          <div className={styles['avatar-section']}>
            <BrandLogo
              src={logoImage}
              alt="Richuel logo"
              className={styles['avatar-image']}
              decoding="async"
            />
            <div className={styles['greeting-text']}>
              <h2 className={styles['greeting-title']}>
                <span className={styles['greeting-title-text']}>Hello! I&apos;m Reese, your guide.</span>
                <span className={styles['wave-emoji']} aria-hidden="true">👋</span>
              </h2>
            </div>
          </div>

          <div className={styles['guidance-text']}>
            <p>I'm here to guide you through managing your finances during your career transition.</p>
          </div>

          <div className={styles['name-input-section']}>
            <label htmlFor="onboarding-name-input" className={styles['input-label']}>
              <span>What&apos;s your name?</span>
              <span className={styles['pencil-emoji']} aria-hidden>✍️</span>
            </label>
            <input
              id="onboarding-name-input"
              type="text"
              placeholder="Enter here"
              value={userName}
              onChange={handleInputChange}
              className={styles['name-input']}
              autoComplete="name"
              aria-label="Your name"
              maxLength={100}
            />
          </div>

          <div className={styles['navigation-buttons']} role="group" aria-label="Navigation">
            <button
              type="button"
              className={styles['back-button']}
              onClick={handleBack}
              aria-label={embedded ? 'Go back' : 'Go back to home'}
            >
              Back
            </button>
            <button
              type="button"
              className={styles['continue-button']}
              onClick={handleContinue}
              disabled={!userName.trim()}
              aria-label="Continue to next step"
            >
              Continue
            </button>
          </div>
        </article>
      </div>
    </main>
  );
};

export default OnboardingInitial; 